import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Modal, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import ThaiDateInput from '../components/ThaiDateInput'

const API = process.env.REACT_APP_API;

const EVENT_TYPES = [
  { value: "activity", label: "กิจกรรม", color: "primary", hex: "#506fd9" },
  { value: "exam", label: "สอบ", color: "danger", hex: "#dc3545" },
  { value: "holiday", label: "วันหยุด", color: "success", hex: "#198754" },
  { value: "meeting", label: "ประชุม", color: "info", hex: "#0dcaf0" },
  { value: "other", label: "อื่นๆ", color: "secondary", hex: "#6c757d" },
];
const typeInfo = (t) => EVENT_TYPES.find((e) => e.value === t) || EVENT_TYPES[0];

const CLASS_OPTIONS = [1, 2, 3, 4, 5, 6]; // c_id 1–6 = ม.1–ม.6

const THAI_MONTHS_FULL = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" }) : "");
// แปลงเป็น YYYY-MM-DD ตามเวลาท้องถิ่น — ห้ามใช้ toISOString (UTC) เพราะวันที่จาก DB
// serialize มาเป็นเที่ยงคืนเวลาไทย จะเพี้ยนถอยหลังไป 1 วัน
const toInputDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
// บวกจำนวนวันให้ string YYYY-MM-DD (ใช้ทำ end แบบ exclusive ของ FullCalendar)
const addDays = (ymd, n) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// ป้ายกำกับขอบเขตการงดเรียน เช่น "คาบ 8–10 · ม.1, ม.2" / "ทั้งวัน · ม.1/2, ม.3/1"
const scopeLabel = (ev, roomMap) => {
  const period = ev.cal_period_start != null
    ? (ev.cal_period_start === ev.cal_period_end ? `คาบ ${ev.cal_period_start}` : `คาบ ${ev.cal_period_start}–${ev.cal_period_end}`)
    : "ทั้งวัน";
  let scope;
  if (ev.cal_room_scope) {
    const ids = ev.cal_room_scope.split(",");
    scope = ids.map((id) => roomMap[id] || `ห้อง #${id}`).join(", ");
  } else if (ev.cal_gender_scope) {
    scope = `เฉพาะ${GENDER_LABEL[ev.cal_gender_scope] || ev.cal_gender_scope}`;
  } else if (ev.cal_class_scope) {
    scope = ev.cal_class_scope.split(",").map((c) => `ม.${c}`).join(", ");
  } else {
    scope = "ทุกระดับชั้น";
  }
  return `${period} · ${scope}`;
};

const GENDER_LABEL = { m: "ห้องชาย", f: "ห้องหญิง" };

const EMPTY_FORM = {
  cal_title: "", cal_description: "", cal_start_date: "", cal_end_date: "", cal_type: "activity",
  cal_no_teaching: false, cal_period_start: "", cal_period_end: "",
  scope_mode: "all", cal_class_ids: [], cal_room_ids: [], cal_gender: "",
};

export default function WorkCalendar() {
  const { setLoading } = useLoading();

  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");
  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (skin === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [events, setEvents] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadEvents = () => {
    setLoading(true);
    axiosInstance.get(`${API}/getCalendarEvents`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", "ไม่สามารถโหลดปฏิทินได้", "error"); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEvents(); }, []);

  // รายการคาบ + ห้องเรียนของปีล่าสุด — คาบเป็นตัวเลือกช่วงคาบ (value = period_order,
  // โหลดไม่ได้ใช้ 1–10), ห้องเป็นตัวเลือกขอบเขตรายห้อง (value = r_id)
  useEffect(() => {
    const load = async () => {
      let yearId = localStorage.getItem("yearId");
      if (!yearId) {
        try {
          const res = await axiosInstance.get(`${API}/getStudyYear`);
          yearId = res.data?.[0]?.year_id;
        } catch { /* ใช้ fallback ด้านล่าง */ }
      }
      try {
        if (!yearId) throw new Error("no year");
        const res = await axiosInstance.get(`${API}/getPeriodList/${yearId}`);
        setPeriods((res.data || []).map((p) => ({
          period_order: p.period_order,
          label: `คาบ ${p.period_order} (${p.period_begin}–${p.period_end})`,
        })));
      } catch {
        setPeriods(Array.from({ length: 10 }, (_, i) => ({ period_order: i + 1, label: `คาบ ${i + 1}` })));
      }
      try {
        if (!yearId) throw new Error("no year");
        const res = await axiosInstance.get(`${API}/getRoomListByYearId/${yearId}`);
        setRooms((res.data || []).map((r) => ({
          r_id: r.r_id, c_id: r.tb_class_c_id, label: `ม.${r.tb_class_c_id}/${r.r_no}`,
        })));
      } catch {
        setRooms([]);
      }
    };
    load();
  }, []);

  // r_id → "ม.X/Y" สำหรับแสดงขอบเขตรายห้องในตาราง
  const roomMap = useMemo(() => {
    const m = {};
    rooms.forEach((r) => { m[r.r_id] = r.label; });
    return m;
  }, [rooms]);

  // เดือนที่ปฏิทินแสดงอยู่ — ตารางรายละเอียดด้านล่างกรองตามเดือนนี้ (เริ่มต้น = เดือนปัจจุบัน)
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });

  // รายการของเดือนที่แสดง — นับรวมรายการหลายวันที่คาบเกี่ยวเดือนนี้
  const monthEvents = useMemo(() => {
    const monthStart = `${viewMonth.y}-${String(viewMonth.m + 1).padStart(2, "0")}-01`;
    const monthEnd = toInputDate(new Date(viewMonth.y, viewMonth.m + 1, 0));
    return events
      .filter((ev) => {
        const s = toInputDate(ev.cal_start_date);
        const e = ev.cal_end_date ? toInputDate(ev.cal_end_date) : s;
        return s <= monthEnd && e >= monthStart;
      })
      .sort((a, b) => toInputDate(a.cal_start_date).localeCompare(toInputDate(b.cal_start_date)));
  }, [events, viewMonth]);

  // แปลงเป็น event ของ FullCalendar — สีตามประเภท, รายการงดเรียนขอบเหลือง
  const fcEvents = useMemo(() => events.map((ev) => {
    const start = toInputDate(ev.cal_start_date);
    const end = addDays(ev.cal_end_date ? toInputDate(ev.cal_end_date) : start, 1); // end เป็น exclusive
    return {
      id: String(ev.cal_id),
      title: (ev.cal_no_teaching ? "งดเรียน · " : "") + ev.cal_title,
      start, end, allDay: true,
      backgroundColor: typeInfo(ev.cal_type).hex,
      borderColor: ev.cal_no_teaching ? "#ffc107" : "transparent",
      extendedProps: { ev },
    };
  }), [events]);

  const openAdd = (dateStr) => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, cal_start_date: dateStr || "" });
    setShowModal(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      cal_title: ev.cal_title, cal_description: ev.cal_description || "",
      cal_start_date: toInputDate(ev.cal_start_date), cal_end_date: toInputDate(ev.cal_end_date), cal_type: ev.cal_type || "activity",
      cal_no_teaching: !!ev.cal_no_teaching,
      cal_period_start: ev.cal_period_start ?? "",
      cal_period_end: ev.cal_period_end ?? "",
      scope_mode: ev.cal_room_scope ? "room" : ev.cal_gender_scope ? "gender" : ev.cal_class_scope ? "class" : "all",
      cal_class_ids: ev.cal_class_scope ? ev.cal_class_scope.split(",").map(Number) : [],
      cal_room_ids: ev.cal_room_scope ? ev.cal_room_scope.split(",").map(Number) : [],
      cal_gender: ev.cal_gender_scope || "",
    });
    setShowModal(true);
  };

  const toggleClass = (cId) => {
    setForm((f) => ({
      ...f,
      cal_class_ids: f.cal_class_ids.includes(cId)
        ? f.cal_class_ids.filter((c) => c !== cId)
        : [...f.cal_class_ids, cId],
    }));
  };

  const toggleRoom = (rId) => {
    setForm((f) => ({
      ...f,
      cal_room_ids: f.cal_room_ids.includes(rId)
        ? f.cal_room_ids.filter((r) => r !== rId)
        : [...f.cal_room_ids, rId],
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.cal_title.trim()) return Swal.fire("แจ้งเตือน", "กรุณาระบุชื่อหัวข้อ/กิจกรรม", "warning");
    if (!form.cal_start_date) return Swal.fire("แจ้งเตือน", "กรุณาระบุวันที่เริ่ม", "warning");
    if (form.cal_end_date && form.cal_end_date < form.cal_start_date)
      return Swal.fire("แจ้งเตือน", "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม", "warning");

    // หน้านี้เป็นเจ้าของฟิลด์งดการเรียนการสอน — ส่งครบทุกครั้ง (server จะ normalize)
    const payload = {
      cal_title: form.cal_title.trim(),
      cal_description: form.cal_description,
      cal_start_date: form.cal_start_date,
      cal_end_date: form.cal_end_date || null,
      cal_type: form.cal_type,
      cal_no_teaching: form.cal_no_teaching,
      cal_period_start: form.cal_no_teaching && form.cal_period_start !== "" ? Number(form.cal_period_start) : null,
      cal_period_end: form.cal_no_teaching && form.cal_period_end !== "" ? Number(form.cal_period_end) : null,
      cal_class_ids: form.cal_no_teaching && form.scope_mode === "class" ? form.cal_class_ids : [],
      cal_room_ids: form.cal_no_teaching && form.scope_mode === "room" ? form.cal_room_ids : [],
      cal_gender: form.cal_no_teaching && form.scope_mode === "gender" ? form.cal_gender : null,
    };
    if (form.cal_no_teaching && form.scope_mode === "room" && payload.cal_room_ids.length === 0)
      return Swal.fire("แจ้งเตือน", "กรุณาเลือกห้องเรียนอย่างน้อย 1 ห้อง", "warning");
    if (form.cal_no_teaching && form.scope_mode === "class" && payload.cal_class_ids.length === 0)
      return Swal.fire("แจ้งเตือน", "กรุณาเลือกระดับชั้นอย่างน้อย 1 ชั้น", "warning");
    if (form.cal_no_teaching && form.scope_mode === "gender" && !payload.cal_gender)
      return Swal.fire("แจ้งเตือน", "กรุณาเลือกเพศของห้องเรียน", "warning");
    const req = editing
      ? axiosInstance.put(`${API}/updateCalendarEvent/${editing.cal_id}`, payload)
      : axiosInstance.post(`${API}/addCalendarEvent`, payload);

    setLoading(true);
    req
      .then(() => { setShowModal(false); Swal.fire("สำเร็จ", editing ? "แก้ไขหัวข้อ/กิจกรรมเรียบร้อย" : "เพิ่มหัวข้อ/กิจกรรมเรียบร้อย", "success"); loadEvents(); })
      .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "บันทึกไม่สำเร็จ", "error"); })
      .finally(() => setLoading(false));
  };

  const handleDelete = (ev) => {
    Swal.fire({
      title: "ยืนยันการลบ", text: `ลบหัวข้อ/กิจกรรม "${ev.cal_title}" ?`, icon: "warning",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก", confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.delete(`${API}/deleteCalendarEvent/${ev.cal_id}`)
        .then(() => { Swal.fire("ลบแล้ว", "ลบหัวข้อ/กิจกรรมเรียบร้อย", "success"); loadEvents(); })
        .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "ลบไม่สำเร็จ", "error"); })
        .finally(() => setLoading(false));
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">งานธุรการ</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ปฏิทินปฏิบัติงาน</li>
            </ol>
            <h4 className="main-title mb-0">ปฏิทินปฏิบัติงาน</h4>
          </div>
          <Button variant="primary" className="d-flex align-items-center gap-2" onClick={() => openAdd()}>
            <i className="ri-add-line fs-18 lh-1"></i> เพิ่มหัวข้อ/กิจกรรม
          </Button>
        </div>

        <p className="text-muted fs-sm">
          รายการที่ระบุ <Badge bg="warning" text="dark">งดการเรียนการสอน</Badge> จะไม่ถูกนับเป็น "ขาดสอน"
          ในรายงานการเข้าสอนของครู ตามวัน / ช่วงคาบ / ระดับชั้นที่กำหนด
        </p>

        <Row className="g-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{ start: "prev,next today", center: "title", end: "" }}
                  locale="th"
                  buttonText={{ today: "วันนี้" }}
                  events={fcEvents}
                  dateClick={(info) => openAdd(info.dateStr)}
                  eventClick={(info) => openEdit(info.event.extendedProps.ev)}
                  datesSet={(arg) => {
                    // currentStart = วันแรกของเดือนที่แสดง → ตารางล่างตามเดือนนี้
                    const d = arg.view.currentStart;
                    setViewMonth({ y: d.getFullYear(), m: d.getMonth() });
                  }}
                  height="auto"
                  dayMaxEventRows={3}
                />
                <small className="text-secondary">คลิกวันที่เพื่อเพิ่มหัวข้อ/กิจกรรม · คลิกรายการเพื่อแก้ไข</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <h6 className="fw-semibold border-bottom pb-2">
                  รายการเดือน{THAI_MONTHS_FULL[viewMonth.m]} {viewMonth.y + 543}
                </h6>
                {monthEvents.length === 0 ? (
                  <div className="text-center text-secondary py-4">ไม่มีหัวข้อ/กิจกรรมในเดือนนี้</div>
                ) : (
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "200px" }}>วันที่</th>
                        <th style={{ width: "100px" }} className="text-center">ประเภท</th>
                        <th>หัวข้อ/กิจกรรม</th>
                        <th style={{ width: "230px" }}>การเรียนการสอน</th>
                        <th style={{ width: "110px" }} className="text-center">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthEvents.map((ev) => {
                        const ti = typeInfo(ev.cal_type);
                        return (
                          <tr key={ev.cal_id}>
                            <td>
                              {formatDate(ev.cal_start_date)}
                              {ev.cal_end_date ? ` - ${formatDate(ev.cal_end_date)}` : ""}
                            </td>
                            <td className="text-center"><Badge bg={ti.color}>{ti.label}</Badge></td>
                            <td>
                              <div className="fw-medium">{ev.cal_title}</div>
                              {ev.cal_description && <small className="text-secondary">{ev.cal_description}</small>}
                            </td>
                            <td>
                              {ev.cal_no_teaching ? (
                                <>
                                  <Badge bg="warning" text="dark">งดการเรียนการสอน</Badge>
                                  <div><small className="text-secondary">{scopeLabel(ev, roomMap)}</small></div>
                                </>
                              ) : (
                                <small className="text-secondary">เรียนตามปกติ</small>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button size="sm" variant="outline-primary" onClick={() => openEdit(ev)}><i className="ri-edit-line"></i></Button>
                                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(ev)}><i className="ri-delete-bin-line"></i></Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{editing ? "แก้ไขหัวข้อ/กิจกรรม" : "เพิ่มหัวข้อ/กิจกรรม"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อหัวข้อ/กิจกรรม <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={form.cal_title} onChange={(e) => setForm({ ...form, cal_title: e.target.value })} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>วันที่เริ่ม <span className="text-danger">*</span></Form.Label>
                  <ThaiDateInput value={form.cal_start_date} onChange={v => setForm({ ...form, cal_start_date: v })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>วันที่สิ้นสุด</Form.Label>
                  <ThaiDateInput value={form.cal_end_date} onChange={v => setForm({ ...form, cal_end_date: v })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ประเภท</Form.Label>
                  <Form.Select value={form.cal_type} onChange={(e) => setForm({ ...form, cal_type: e.target.value })}>
                    {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>รายละเอียด</Form.Label>
                  <Form.Control as="textarea" rows={1} value={form.cal_description} onChange={(e) => setForm({ ...form, cal_description: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>

            <Form.Check
              type="switch"
              id="no-teaching-switch"
              className="mb-2"
              label="หยุดการเรียนการสอน (ไม่นับขาดสอนในรายงานการเข้าสอน)"
              checked={form.cal_no_teaching}
              onChange={(e) => setForm({ ...form, cal_no_teaching: e.target.checked })}
            />

            {form.cal_no_teaching && (
              <div className="border rounded p-3 bg-light-subtle">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>จากคาบ</Form.Label>
                      <Form.Select value={form.cal_period_start} onChange={(e) => setForm({ ...form, cal_period_start: e.target.value })}>
                        <option value="">ทั้งวัน</option>
                        {periods.map((p) => <option key={p.period_order} value={p.period_order}>{p.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ถึงคาบ</Form.Label>
                      <Form.Select value={form.cal_period_end} onChange={(e) => setForm({ ...form, cal_period_end: e.target.value })}>
                        <option value="">ทั้งวัน</option>
                        {periods.map((p) => <option key={p.period_order} value={p.period_order}>{p.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-1">
                  <Form.Label>ขอบเขตที่งดเรียน</Form.Label>
                  <div className="d-flex flex-wrap gap-3 mb-2">
                    {[["all", "ทั้งโรงเรียน"], ["class", "เลือกระดับชั้น"], ["room", "เลือกห้องเรียน"], ["gender", "เลือกเพศห้อง"]].map(([mode, label]) => (
                      <Form.Check
                        key={mode}
                        type="radio"
                        name="scope-mode"
                        id={`scope-${mode}`}
                        label={label}
                        checked={form.scope_mode === mode}
                        onChange={() => setForm({ ...form, scope_mode: mode })}
                      />
                    ))}
                  </div>
                  {form.scope_mode === "class" && (
                    <div className="d-flex flex-wrap gap-3">
                      {CLASS_OPTIONS.map((cId) => (
                        <Form.Check
                          key={cId}
                          type="checkbox"
                          id={`class-${cId}`}
                          label={`ม.${cId}`}
                          checked={form.cal_class_ids.includes(cId)}
                          onChange={() => toggleClass(cId)}
                        />
                      ))}
                    </div>
                  )}
                  {form.scope_mode === "gender" && (
                    <div className="d-flex flex-wrap gap-3">
                      {[["m", "ห้องชาย"], ["f", "ห้องหญิง"]].map(([g, label]) => (
                        <Form.Check
                          key={g}
                          type="radio"
                          name="gender-scope"
                          id={`gender-${g}`}
                          label={label}
                          checked={form.cal_gender === g}
                          onChange={() => setForm({ ...form, cal_gender: g })}
                        />
                      ))}
                    </div>
                  )}
                  {form.scope_mode === "room" && (
                    rooms.length === 0 ? (
                      <small className="text-secondary">ไม่พบรายชื่อห้องเรียนของปีการศึกษาปัจจุบัน</small>
                    ) : (
                      // จัดกลุ่มห้องตามระดับชั้น แถวละชั้น
                      CLASS_OPTIONS.map((cId) => {
                        const classRooms = rooms.filter((r) => r.c_id === cId);
                        if (classRooms.length === 0) return null;
                        return (
                          <div key={cId} className="d-flex flex-wrap gap-3 mb-1">
                            {classRooms.map((r) => (
                              <Form.Check
                                key={r.r_id}
                                type="checkbox"
                                id={`room-${r.r_id}`}
                                label={r.label}
                                checked={form.cal_room_ids.includes(r.r_id)}
                                onChange={() => toggleRoom(r.r_id)}
                              />
                            ))}
                          </div>
                        );
                      })
                    )
                  )}
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" type="submit">บันทึก</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </React.Fragment>
  );
}
